/**
 * Utility functions for handling URL search parameters safely
 * Ensures values are properly sanitized and don't contain unwanted quotes or whitespace
 */

/**
 * Sanitizes a value to be used in URL search params
 * Removes quotes, trims whitespace, and ensures it's a clean string
 */
export function sanitizeSearchParamValue(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }
  
  // Convert to string and trim
  let str = String(value).trim()
  
  // Remove surrounding quotes (both single and double) that might have been added
  str = str.replace(/^["']+|["']+$/g, '')
  
  // Trim again after removing quotes
  str = str.trim()
  
  // Return undefined for empty strings
  return str || undefined
}

/**
 * Sanitizes all values in a search params object
 */
export function sanitizeSearchParams<T extends Record<string, unknown>>(
  params: T
): Record<string, string | undefined> {
  const sanitized: Record<string, string | undefined> = {}
  
  for (const [key, value] of Object.entries(params)) {
    const sanitizedValue = sanitizeSearchParamValue(value)
    if (sanitizedValue !== undefined) {
      sanitized[key] = sanitizedValue
    }
  }
  
  return sanitized
}

/**
 * Removes undefined values from a search params object
 * Useful for keeping URLs clean
 */
export function removeUndefinedParams<T extends Record<string, unknown>>(
  params: T
): Partial<T> {
  const cleaned: Partial<T> = {}
  
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key as keyof T] = value as T[keyof T]
    }
  }
  
  return cleaned
}

/**
 * Combines sanitization and removal of undefined values
 */
export function cleanSearchParams<T extends Record<string, unknown>>(
  params: T
): Record<string, string> {
  const sanitized = sanitizeSearchParams(params)
  const cleaned: Record<string, string> = {}
  
  for (const [key, value] of Object.entries(sanitized)) {
    if (value !== undefined) {
      cleaned[key] = value
    }
  }
  
  return cleaned
}

